/**
 * Get the optimistic-lock version for a report phase.
 * @param {Object} report - Work report data.
 * @param {'plan'|'summary'} phase - Report phase.
 * @returns {number} Phase version, defaulting to zero for a new phase.
 */
export function getPhaseVersion(report, phase) {
  const phaseValue = report?.[phase]

  if (typeof phaseValue === 'number') {
    return phaseValue
  }

  if (typeof phaseValue?.version === 'number') {
    return phaseValue.version
  }

  const version = report?.[`${phase}Version`] ?? report?.versions?.[phase]
  return typeof version === 'number' ? version : 0
}

/**
 * Get the aggregate optimistic-lock version for a daily work report.
 * Backend write commands compare this value, not the phase version.
 * @param {Object} report - Work report data.
 * @returns {number} Aggregate report version.
 */
export function getReportOptimisticVersion(report) {
  const version = report?.optimisticVersion ?? report?.version
  const normalized = Number(version)
  return Number.isFinite(normalized) ? normalized : 0
}

const NO_PLAN_REASON_PREFIX = 'No-plan reason:'

/**
 * Extract the no-plan reason embedded by the backend in planSupplement.
 * @param {string} supplement - Plan supplement text.
 * @returns {string} No-plan reason.
 */
export function extractNoPlanReason(supplement) {
  const line = String(supplement || '')
    .split(/\r?\n/)
    .find((item) => item.trim().startsWith(NO_PLAN_REASON_PREFIX))

  return line ? line.trim().slice(NO_PLAN_REASON_PREFIX.length).trim() : ''
}

/**
 * Remove backend-embedded no-plan reason from user-editable supplement text.
 * @param {string} supplement - Plan supplement text.
 * @returns {string} Supplement without the backend no-plan marker.
 */
export function stripNoPlanReasonFromSupplement(supplement) {
  return String(supplement || '')
    .split(/\r?\n/)
    .filter((line) => !line.trim().startsWith(NO_PLAN_REASON_PREFIX))
    .join('\n')
    .trim()
}

const TODO_PRIORITY_VALUE = Object.freeze({
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4
})

/**
 * Normalize todo priority values to the backend ReportTodoSnapshotDTO scalar type.
 * @param {number|string|null|undefined} priority - Todo priority from API or local state.
 * @returns {number|null} Numeric priority, or null when unknown.
 */
export function normalizeTodoPriority(priority) {
  if (priority === null || priority === undefined) return null

  if (typeof priority === 'number') {
    return Number.isFinite(priority) ? priority : null
  }

  const text = String(priority).trim()
  if (!text) return null

  const numeric = Number(text)
  if (Number.isFinite(numeric)) return numeric

  return TODO_PRIORITY_VALUE[text.toUpperCase()] ?? null
}

function formatDateAsLocalDateTime(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

const SHANGHAI_TIME_ZONE = 'Asia/Shanghai'

function formatOffsetDateTimeInShanghai(text) {
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SHANGHAI_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23'
  }).formatToParts(date)
  const value = (type) => parts.find((part) => part.type === type)?.value
  const hour = value('hour') === '24' ? '00' : value('hour')
  return `${value('year')}-${value('month')}-${value('day')} ${hour}:${value('minute')}`
}

/**
 * Normalize date-time values to the ISO local format accepted by Java LocalDateTime.
 * @param {Date|string|null|undefined} value - Date-time value from todo APIs or local state.
 * @returns {string|undefined} ISO local date-time string, or undefined for blank values.
 */
export function normalizeReportLocalDateTime(value) {
  if (value === null || value === undefined || value === '') return undefined

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : formatDateAsLocalDateTime(value)
  }

  const text = String(value).trim()
  if (!text) return undefined

  const dateOnly = text.match(/^(\d{4}-\d{2}-\d{2})$/)
  if (dateOnly) return `${dateOnly[1]}T00:00:00`

  const localDateTime = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?)(?:Z|[+-]\d{2}:\d{2})?$/)
  if (localDateTime) return `${localDateTime[1]}T${localDateTime[2]}`

  return text
}

/**
 * Format work-report date-time values in the Shanghai business timezone.
 * Backend LocalDateTime strings are already business-local and are displayed as-is.
 * @param {Date|string|null|undefined} value - Date-time value from report APIs.
 * @returns {string} Display value in yyyy-MM-dd HH:mm format, or -- for blank values.
 */
export function formatReportDisplayDateTime(value) {
  if (value === null || value === undefined || value === '') return '--'

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '--' : formatOffsetDateTimeInShanghai(value.toISOString()) || '--'
  }

  const text = String(value).trim()
  if (!text) return '--'

  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
    return formatOffsetDateTimeInShanghai(text) || '--'
  }

  const normalized = text.replace('T', ' ')
  const dateTime = normalized.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})/)
  if (dateTime) return `${dateTime[1]} ${dateTime[2]}:${dateTime[3]}`

  const dateOnly = normalized.match(/^(\d{4}-\d{2}-\d{2})/)
  return dateOnly ? dateOnly[1] : normalized
}

/**
 * Format only the HH:mm part of a work-report date-time value.
 * @param {Date|string|null|undefined} value - Date-time value from report APIs.
 * @returns {string} Display time, or -- for blank values.
 */
export function formatReportDisplayTime(value) {
  const formatted = formatReportDisplayDateTime(value)
  if (!formatted || formatted === '--') return '--'
  const time = formatted.match(/(?:^|\s)(\d{2}:\d{2})$/)
  return time ? time[1] : formatted
}

/**
 * Normalize a todo or todo snapshot before sending it inside a work report payload.
 * @param {Object|null|undefined} todo - Todo data or existing todo snapshot.
 * @returns {Object|null} Backend DTO-compatible todo snapshot.
 */
export function normalizeReportTodoSnapshot(todo) {
  if (!todo || typeof todo !== 'object') return null

  return {
    todoId: todo.todoId ?? todo.id,
    title: todo.title,
    description: todo.description,
    priority: normalizeTodoPriority(todo.priority),
    dueDate: normalizeReportLocalDateTime(todo.dueDate),
    status: todo.status,
    completeTime: normalizeReportLocalDateTime(todo.completeTime),
    updateTime: normalizeReportLocalDateTime(todo.updateTime),
    annotationCount: todo.annotationCount
  }
}

const WORK_REPORT_STATUS_META = Object.freeze({
  DRAFT: { label: '草稿', color: 'bg-slate-100 text-slate-700' },
  SUBMITTED: { label: '已提交', color: 'bg-emerald-50 text-emerald-700' },
  REQUIRED: { label: '应填写', color: 'bg-blue-50 text-blue-700' },
  AUTO_EXEMPT: { label: '自动豁免', color: 'bg-violet-50 text-violet-700' },
  MANUAL_EXEMPT: { label: '人工豁免', color: 'bg-violet-50 text-violet-700' },
  EXEMPT: { label: '豁免', color: 'bg-violet-50 text-violet-700' },
  PENDING_STATUS_CONFIRMATION: { label: '待状态确认', color: 'bg-sky-50 text-sky-700' },
  LATE: { label: '迟交', color: 'bg-amber-50 text-amber-700' },
  MISSING: { label: '缺交', color: 'bg-rose-50 text-rose-700' },
  PENDING_SUPPLEMENT: { label: '待补充', color: 'bg-orange-50 text-orange-700' },
  PENDING_EVALUATION: { label: '待评价', color: 'bg-blue-50 text-blue-700' },
  NO_SUPERVISOR: { label: '无直属主管', color: 'bg-slate-100 text-slate-700' }
})

const PHASE_LABEL = Object.freeze({
  PLAN: '计划',
  SUMMARY: '总结'
})

const TODO_STATUS_LABEL = Object.freeze({
  0: '待办',
  1: '已完成',
  2: '已逾期',
  3: '已取消',
  4: '进行中',
  PENDING: '待办',
  COMPLETED: '已完成',
  OVERDUE: '已逾期',
  CANCELLED: '已取消',
  IN_PROGRESS: '进行中'
})

const TODO_STATUS_CODE = Object.freeze({
  PENDING: 0,
  COMPLETED: 1,
  OVERDUE: 2,
  CANCELLED: 3,
  IN_PROGRESS: 4
})

const PRIORITY_LABEL = Object.freeze({
  1: '低',
  2: '中',
  3: '高',
  4: '紧急'
})

const WORK_ITEM_SOURCE_LABEL = Object.freeze({
  TODO: '待办',
  MANUAL: '手工项',
  CARRYOVER: '昨日遗留',
  UNPLANNED: '计划外'
})

const WORK_ITEM_RESULT_LABEL = Object.freeze({
  IN_PROGRESS: '进行中',
  COMPLETED: '已完成',
  PARTIALLY_COMPLETED: '部分完成',
  NOT_STARTED: '未开始',
  CANCELLED: '已取消',
  TRANSFERRED: '已转交'
})

function enumKey(value) {
  return String(value ?? '').trim().toUpperCase()
}

/**
 * Get display metadata for work report status-like values.
 * @param {string} status - Backend enum or UI-derived exception status.
 * @returns {{label: string, color: string}} Chinese status label and badge color.
 */
export function workReportStatusMeta(status) {
  return WORK_REPORT_STATUS_META[enumKey(status)] || {
    label: '未知状态',
    color: 'bg-slate-100 text-slate-700'
  }
}

/**
 * Translate report phase enum values to Chinese labels.
 * @param {string} phase - Backend ReportPhase enum.
 * @returns {string} Chinese phase label.
 */
export function workReportPhaseLabel(phase) {
  return PHASE_LABEL[enumKey(phase)] || '未知阶段'
}

/**
 * Translate todo status values from notify APIs to Chinese labels.
 * @param {number|string|null|undefined} status - Todo status code, enum name, or code string.
 * @returns {string} Chinese todo status label.
 */
export function workReportTodoStatusLabel(status) {
  if (status === null || status === undefined || status === '') return '未知状态'
  const numeric = Number(status)
  if (Number.isFinite(numeric) && TODO_STATUS_LABEL[numeric]) return TODO_STATUS_LABEL[numeric]
  return TODO_STATUS_LABEL[enumKey(status)] || '未知状态'
}

/**
 * Normalize todo status values to the backend numeric status code.
 * @param {number|string|null|undefined} status - Todo status code, enum name, or code string.
 * @returns {number|null} Numeric status code, or null when unknown.
 */
export function normalizeTodoStatusCode(status) {
  if (status === null || status === undefined || status === '') return null

  const numeric = Number(status)
  if (Number.isFinite(numeric)) return numeric

  return TODO_STATUS_CODE[enumKey(status)] ?? null
}

/**
 * Translate todo priority values to Chinese labels.
 * @param {number|string|null|undefined} priority - Todo priority code or enum name.
 * @returns {string} Chinese priority label.
 */
export function workReportPriorityLabel(priority) {
  const normalized = normalizeTodoPriority(priority)
  return normalized ? (PRIORITY_LABEL[normalized] || '未知优先级') : '未知优先级'
}

/**
 * Translate work item source enum values to Chinese labels.
 * @param {string} source - Backend ReportWorkItemSource enum.
 * @returns {string} Chinese source label.
 */
export function workReportItemSourceLabel(source) {
  return WORK_ITEM_SOURCE_LABEL[enumKey(source)] || '未知来源'
}

/**
 * Translate work item result enum values to Chinese labels.
 * @param {string} result - Backend ReportWorkItemResult enum.
 * @returns {string} Chinese result label.
 */
export function workReportItemResultLabel(result) {
  return WORK_ITEM_RESULT_LABEL[enumKey(result)] || '未知结果'
}

/**
 * Return the display label for the report's direct supervisor.
 * @param {Object} report - Work report data.
 * @returns {string} Supervisor name or a clear unassigned-state label.
 */
export function supervisorLabel(report) {
  const supervisor = report?.supervisorName
    || report?.directSupervisorName
    || report?.supervisor?.name

  return typeof supervisor === 'string' && supervisor.trim()
    ? supervisor
    : '暂未分配直属主管'
}

function createRandomBytes(byteCount) {
  const bytes = typeof Uint8Array === 'function'
    ? new Uint8Array(byteCount)
    : Array.from({ length: byteCount }, () => 0)
  const browserCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined

  if (typeof Uint8Array === 'function' && bytes instanceof Uint8Array && browserCrypto && typeof browserCrypto.getRandomValues === 'function') {
    return browserCrypto.getRandomValues(bytes)
  }

  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256)
  }

  return bytes
}

function createFallbackClientRequestId() {
  const bytes = createRandomBytes(16)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}

/**
 * Create an idempotency key for one logical save attempt.
 * @returns {string} Client request ID.
 */
export function createClientRequestId() {
  const browserCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  const randomUUID = browserCrypto?.randomUUID

  if (typeof randomUUID === 'function') {
    return randomUUID.call(browserCrypto)
  }

  return createFallbackClientRequestId()
}
